import { isNative, shareFile } from '../native.ts';
import { readableParagraphs } from '@tc/domain';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDismiss } from '../dismiss.ts';
import type { AttachmentDTO, MessageDTO } from '@tc/contracts';
import { fmtBytes } from '../attachments.ts';
import { messageAction, toggleOriginal, useStore } from '../store.ts';
import { formatClock, formatTime, languageName, translate, useT, useUiLocale, type MsgKey } from '../i18n.ts';
import { Linkified, Seal, displayName, systemText } from './common.tsx';
import { EmojiPicker, QUICK_REACTIONS } from './EmojiPicker.tsx';
import { cloudVoice, speakMessage, stopSpeaking, ttsSupported } from '../tts.ts';
import { SuggestDialog } from './Suggestions.tsx';
import { SummaryDialog } from './SummaryDialog.tsx';
import { ImageTextDialog } from './ImageTextDialog.tsx';

interface Props {
  m: MessageDTO;
  showSender: boolean;
  /** Last message in a run from the same person (gets the avatar and the bubble tail). */
  lastInRun?: boolean;
  firstInRun?: boolean;
  inGroup?: boolean;
  /** Index of the sender among the chat's members: picks their name colour. */
  nameColor?: number;
  canModerate: boolean;
  onReply: (m: MessageDTO) => void;
  /** Reply to `m` with a pre-filled clarification question (reader's language). */
  onClarify: (m: MessageDTO, question: string) => void;
  onEdit: (m: MessageDTO) => void;
  onJump: (id: string) => void;
}

function TranslationLine({ m, mine, onClarify }: { m: MessageDTO; mine: boolean; onClarify: (m: MessageDTO, question: string) => void }) {
  const t = useT();
  const ui = useUiLocale();
  // Your own message shows what you wrote; the translation is one click away. Others' show the translation.
  const showingOriginal = useStore((s) => !!s.showOriginal[m.id] !== mine);
  const readingLocale = useStore((s) => s.me?.readingLocale ?? 'en');
  const tr = m.translation;
  const lang = languageName(ui, m.sourceLocale);
  const [busy, setBusy] = useState(false);
  const retranslate = async () => {
    setBusy(true);
    await messageAction('POST', `/api/messages/${m.id}/translate`, { force: true }).catch(() => {});
    setBusy(false);
  };

  if (!tr) {
    // Sender's own language or a language the reader reads: just show the seal.
    return (
      <div className="tr-line">
        <Seal locale={m.sourceLocale} small title={lang} />
        {m.mixedLanguage && <span className="muted">{t('mixedLanguage')}</span>}
      </div>
    );
  }
  if (tr.status === 'pending') {
    return (
      <div className="tr-line">
        <Seal locale={m.sourceLocale} small title={lang} />
        <span className="tr-status pending">{tr.errorCode ? t('translationRetrying') : t('translating')}</span>
      </div>
    );
  }
  if (tr.status === 'failed') {
    const key: MsgKey = tr.errorCode === 'translation_disabled' ? 'translationDisabled' : tr.errorCode === 'budget_exceeded' ? 'budgetExceeded' : 'translationFailed';
    return (
      <div className="tr-line">
        <Seal locale={m.sourceLocale} small title={lang} />
        <span className="tr-status failed" role="status">{t(key)}</span>
        {tr.errorCode !== 'translation_disabled' && (
          <button className="link-btn" onClick={retranslate} disabled={busy}>{t('retry')}</button>
        )}
      </div>
    );
  }
  const warnings = tr.flags.filter((f) => f.severity === 'warning');
  // Prefer the model's concrete question; otherwise a generic "please confirm" in the reader's language.
  const question = warnings.find((f) => f.question)?.question ?? translate(readingLocale, 'clarifyGeneric');
  return (
    <>
      <div className="tr-line">
        <Seal locale={m.sourceLocale} small title={lang} />
        <span className="muted">{showingOriginal ? t('originalIn', { lang }) : t('translatedFrom', { lang })}</span>
        <button className="link-btn" onClick={() => toggleOriginal(m.id)} aria-pressed={showingOriginal}>
          {showingOriginal ? t('showTranslation') : t('showOriginal')}
        </button>
      </div>
      {/* Review hints and the clarification question are for the reader, never for the author. */}
      {warnings.length > 0 && !showingOriginal && !mine && (
        <div className="tr-flags" role="note">
          <strong>{t('review')}</strong>
          <ul>
            {warnings.map((f, i) => (
              <li key={i}>{t(`flag_${f.code}` as MsgKey, { d: f.detail ?? '' })}</li>
            ))}
          </ul>
          <div className="tr-flag-actions">
            <button className="btn small" onClick={() => onClarify(m, question)}>{t('askClarification')}</button>
            <button className="link-btn" onClick={retranslate} disabled={busy}>{t('retranslate')}</button>
          </div>
        </div>
      )}
    </>
  );
}

function CopyIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
      <rect x="5.5" y="5.5" width="8" height="8.5" rx="1.6" />
      <path d="M10.5 3.2V3a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 3v6.5A1.5 1.5 0 0 0 4 11h.3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8.5l3.2 3L13 4.5" />
    </svg>
  );
}

/**
 * Copy text. The async Clipboard API only exists in secure contexts (https or localhost); when the
 * app is opened over plain http on the LAN (e.g. from a phone during testing) fall back to execCommand.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

export const Message = memo(function Message({ m, showSender, firstInRun = true, lastInRun = true, inGroup = false, nameColor, canModerate, onReply, onClarify, onEdit, onJump }: Props) {
  const t = useT();
  const ui = useUiLocale();
  const meId = useStore((s) => s.me?.id);
  const sender = useStore((s) => (m.senderId ? s.users[m.senderId] : undefined));
  const showingOriginal = useStore((s) => !!s.showOriginal[m.id] !== (m.senderId === meId));
  const [picker, setPicker] = useState(false);
  const [copied, setCopied] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  // Touch devices have no hover: a tap on the bubble opens the action bar.
  const [toolsOpen, setToolsOpen] = useState(false);
  // Phones: a tap anywhere outside this message closes its action bar.
  const articleRef = useRef<HTMLElement>(null);
  const closeTools = useCallback(() => setToolsOpen(false), []);
  useDismiss(articleRef, toolsOpen && !picker, closeTools);
  const myName = useStore((s) => s.me?.displayName);
  const mentionIds = m.mentions ?? [];
  const members = useStore((s) => (mentionIds.length ? s.conversations[m.conversationId]?.members : undefined));
  const mentionNames = useMemo(() => members?.filter((x) => mentionIds.includes(x.id)).map((x) => x.displayName), [members, mentionIds]);

  if (m.kind === 'system') {
    return (
      <div className="sys-msg" role="note">
        {systemText(m, ui)}
        {m.system?.event === 'talk_on' && m.system.actorId !== meId && (
          <button
            type="button"
            className="link-btn small"
            onClick={() => window.dispatchEvent(new CustomEvent('tc:talk-on', { detail: m.conversationId }))}
          >
            {t('talkJoin')}
          </button>
        )}
      </div>
    );
  }

  const mine = m.senderId === meId;
  const deleted = !!m.deletedAt;
  const mentionsMe = !!meId && mentionIds.includes(meId);
  const tr = m.translation;
  const text = deleted ? '' : tr?.status === 'ready' && tr.body && !showingOriginal ? tr.body : (m.body ?? '');
  const showingTranslation = !deleted && tr?.status === 'ready' && !showingOriginal;

  const react = (emoji: string, on: boolean) =>
    void messageAction(on ? 'POST' : 'DELETE', on ? `/api/messages/${m.id}/reactions` : `/api/messages/${m.id}/reactions/${encodeURIComponent(emoji)}`, on ? { emoji } : undefined).catch(() => {});

  const copy = async () => {
    if (await copyText(text)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <article
      tabIndex={0}
      className={`msg ${mine ? 'mine' : 'theirs'} ${deleted ? 'deleted' : ''} ${toolsOpen ? 'tools-open' : ''} ${mentionsMe ? 'mentions-me' : ''} ${firstInRun ? 'first' : ''} ${lastInRun ? 'last' : ''} ${inGroup ? 'in-group' : ''} nc-${(nameColor ?? nameHue(m.senderId ?? '')) % NAME_COLORS}`}
      id={`m-${m.id}`}
      ref={articleRef}
      aria-label={sender?.displayName}
    >
      {inGroup && !mine && lastInRun && (
        <span className="msg-avatar" aria-hidden="true">{initialsOf(sender?.displayName ?? '?')}</span>
      )}
      <div
        className="bubble"
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('a,button')) return;
          if (window.matchMedia('(hover: none)').matches) setToolsOpen((v) => !v);
        }}
      >
        {!deleted && (
          <button className={`copy-btn ${copied ? 'done' : ''}`} onClick={() => void copy()} aria-label={copied ? t('copied') : t('copy')} title={copied ? t('copied') : t('copy')}>
            {copied ? <CheckIcon /> : <CopyIcon />}
          </button>
        )}
        {showSender && !mine && (
          <div className="msg-sender">
            <span className="name">{sender?.displayName ?? t('someone')}</span>
            {sender?.organizationName && <span className="org">{sender.organizationName}</span>}
          </div>
        )}
        {m.replyTo && (
          <button className="reply-quote" onClick={() => onJump(m.replyTo!.id)}>
            <span className="name">{displayName(m.replyTo.senderId, ui)}</span>
            {m.replyTo.quote ? (
              <span className="snippet quote" lang={m.replyTo.quoteLocale ?? undefined}>❝{m.replyTo.quote}❞</span>
            ) : (
              <span className="snippet" title={m.replyTo.translatedBody && m.replyTo.body ? m.replyTo.body : undefined}>
                {m.replyTo.deleted ? t('deleted') : (m.replyTo.translatedBody ?? m.replyTo.body ?? '…')}
              </span>
            )}
          </button>
        )}
        {!deleted && m.attachment && <Attachment a={m.attachment} />}
        {deleted ? (
          <p className="body muted">{t('deleted')}</p>
        ) : text ? (
          <p className={`body ${showingTranslation ? 'is-translation' : ''}`} lang={showingTranslation ? tr!.targetLocale : m.sourceLocale}>
            <Linkified text={readableParagraphs(text)} names={mentionNames} myName={myName} />
          </p>
        ) : null}
        {!deleted && m.audio && (
          <div className="msg-audio">
            <audio controls preload="none" src={`/api/media/${m.audio.id}`} aria-label={t('voiceMessage')} />
          </div>
        )}
        {!deleted && m.spoken && text && (ttsSupported() || cloudVoice(showingTranslation ? tr!.targetLocale : m.sourceLocale)) && (
          <button
            type="button"
            className="listen-btn"
            onClick={() => {
              stopSpeaking();
              void speakMessage(m.id, text, showingTranslation ? tr!.targetLocale : m.sourceLocale);
            }}
          >
            🔊 {t('listen')}
          </button>
        )}
        {!deleted && !!m.body?.trim() && <TranslationLine m={m} mine={mine} onClarify={onClarify} />}
        <div className="msg-meta">
          <time dateTime={m.createdAt} title={formatTime(m.createdAt, ui)}>{formatClock(m.createdAt, ui)}</time>
          {m.pinnedAt && !deleted && <span className="pinned-mark" title={t('pinned')} aria-label={t('pinned')}>📌</span>}
          {m.editedAt && !deleted && <span className="edited">{t('edited')}</span>}
          {mine && !deleted && <ReadReceipt m={m} meId={meId} />}
        </div>
      </div>
      {m.reactions.length > 0 && (
        <div className="reactions">
          {m.reactions.map((r) => {
            const on = !!meId && r.userIds.includes(meId);
            const names = r.userIds.map((u) => displayName(u, ui)).join(', ');
            return (
              <button key={r.emoji} className={`reaction ${on ? 'on' : ''}`} onClick={() => react(r.emoji, !on)} title={names} aria-pressed={on} aria-label={`${r.emoji} ${names}`}>
                <span className="e">{r.emoji}</span>
                <span className="n">{r.userIds.length}</span>
              </button>
            );
          })}
        </div>
      )}
      {!deleted && (
        <div className="msg-tools" role="toolbar" aria-label="Message actions" onClick={(e) => (e.target as HTMLElement).closest('.tool:not(.keep-open)') && !picker && setToolsOpen(false)}>
          {QUICK_REACTIONS.slice(0, 3).map((e) => (
            <button key={e} className="tool emoji" onClick={() => react(e, true)} aria-label={`${t('react')} ${e}`}>{e}</button>
          ))}
          <button className="tool keep-open" data-popup-toggle onClick={() => setPicker((v) => !v)} aria-label={t('react')} title={t('react')}>☺︎+</button>
          <button className="tool" onClick={() => onReply(m)} title={t('reply')}>{t('reply')}</button>
          {text && <button className="tool touch-only" onClick={() => void copy()}>{copied ? t('copied') : t('copy')}</button>}
          {!mine && showingTranslation && (
            <button className="tool" onClick={() => setSuggesting(true)} title={t('suggestTitle')}>{t('suggestShort')}</button>
          )}
          {(ttsSupported() || cloudVoice(showingTranslation ? tr!.targetLocale : m.sourceLocale)) && text && (
            <button
              className="tool"
              onClick={() => {
                stopSpeaking();
                void speakMessage(m.id, text, showingTranslation ? tr!.targetLocale : m.sourceLocale);
              }}
              title={t('readAloud')}
            >
              {t('readAloud')}
            </button>
          )}
          <button className="tool" onClick={() => void messageAction('POST', `/api/messages/${m.id}/pin`, { on: !m.pinnedAt }).catch(() => {})}>
            {m.pinnedAt ? t('unpin') : t('pin')}
          </button>
          {mine && m.kind === 'text' && <button className="tool" onClick={() => onEdit(m)}>{t('edit')}</button>}
          {(mine || canModerate) && (
            <button
              className="tool danger"
              onClick={() => {
                if (confirm(t('confirmDelete'))) void messageAction('DELETE', `/api/messages/${m.id}`).catch(() => {});
              }}
            >
              {t('delete')}
            </button>
          )}
          {picker && (
            <EmojiPicker
              onPick={(e) => {
                react(e, true);
                setPicker(false);
              }}
              onClose={() => setPicker(false)}
            />
          )}
        </div>
      )}
      {suggesting && <SuggestDialog m={m} onClose={() => setSuggesting(false)} />}
    </article>
  );
});

const NAME_COLORS = 10;

/** Fallback colour for someone no longer in the chat: steady per person. */
function nameHue(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % NAME_COLORS;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = [...parts[0]!][0] ?? '';
  // Chinese names: one character is enough; Latin names: first + last initial.
  if (/\p{Script=Han}/u.test(first)) return first;
  return (first + (parts.length > 1 ? ([...parts[parts.length - 1]!][0] ?? '') : '')).toUpperCase();
}

function Attachment({ a }: { a: AttachmentDTO }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(false);
  const imageTextOn = useStore((s) => s.imageText !== 'disabled');
  const src = `/api/media/${a.id}`;
  if (a.kind === 'file') {
    return (
      <>
        <a
          className="msg-file"
          href={src}
          download={a.name ?? undefined}
          onClick={(e) => {
            // Phone app: the phone's own "save / open in" sheet instead of a browser download.
            if (!isNative) return;
            e.preventDefault();
            void shareFile(src, a.name ?? 'document.pdf');
          }}
        >
          <span className="file-icon" aria-hidden="true">PDF</span>
          <span className="mf-name">{a.name ?? 'document.pdf'}</span>
          <span className="mf-size">{fmtBytes(a.byteSize)} · {t('download')}</span>
        </a>
        <button type="button" className="link-btn small" onClick={() => setOpen(true)}>{t('sumPdf')}</button>
        {open && <SummaryDialog url={`/api/media/${a.id}/summary`} title={t('sumDocTitle', { name: a.name ?? 'PDF' })} onClose={() => setOpen(false)} />}
      </>
    );
  }
  const ratio = a.width && a.height ? `${a.width} / ${a.height}` : undefined;
  return (
    <>
      <button type="button" className="msg-image" onClick={() => setOpen(true)} aria-label={t('openImage')}>
        <img src={src} alt={a.name ?? ''} loading="lazy" decoding="async" style={ratio ? { aspectRatio: ratio } : undefined} />
      </button>
      {imageTextOn && (
        <button type="button" className="link-btn small" onClick={() => setReading(true)}>{t('imgTextButton')}</button>
      )}
      {reading && <ImageTextDialog mediaId={a.id} onClose={() => setReading(false)} />}
      {open && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={a.name ?? t('openImage')} onClick={() => setOpen(false)} onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}>
          <img src={src} alt={a.name ?? ''} onClick={(e) => e.stopPropagation()} />
          <div className="lightbox-bar" onClick={(e) => e.stopPropagation()}>
            <a className="btn small" href={`${src}?download=1`} download={a.name ?? undefined}>{t('download')}</a>
            <button type="button" className="btn small" autoFocus onClick={() => setOpen(false)}>{t('close')}</button>
          </div>
        </div>
      )}
    </>
  );
}

/** ✓ sent · ✓✓ read by everyone (group: "read by 2 of 3", names on hover). */
function ReadReceipt({ m, meId }: { m: MessageDTO; meId: string | undefined }) {
  const t = useT();
  const members = useStore((s) => s.conversations[m.conversationId]?.members);
  const others = (members ?? []).filter((x) => x.id !== meId && !x.disabled);
  if (!others.length) return null;
  const readers = others.filter((x) => x.lastReadAt && x.lastReadAt >= m.createdAt);
  const all = readers.length === others.length;
  const label = !readers.length
    ? t('receiptSent')
    : others.length === 1
      ? t('receiptRead')
      : all
        ? t('receiptReadAll')
        : t('receiptReadBy', { n: readers.length, total: others.length });
  return (
    <span className={`receipt ${readers.length ? 'read' : ''} ${all ? 'all' : ''}`} title={readers.length ? `${label}: ${readers.map((r) => r.displayName).join(', ')}` : label} aria-label={label}>
      {readers.length ? '✓✓' : '✓'}
      {others.length > 1 && readers.length > 0 && !all && <span className="receipt-n">{readers.length}</span>}
    </span>
  );
}
